/**
 * `SkillsStep` — ADR-0049 step 1 (Skills · detect & match). The
 * skills-first reveal: the seven default skills + a detection-driven
 * suggestion panel. ADR-0050 §2 removes the merchant-facing "Analyze
 * my site" button and replaces the merchant-clickable LLM trigger
 * with a detection-only baseline; the dormant ADR-0040 LLM pipeline
 * composes in automatically via `mergeLlmIntoBaseline` when (and only
 * when) `PROFILE_EXTRACTION_ENABLED` flips and a result is on file.
 *
 * Composition (top → bottom):
 *   - `NumLabel` "Step 2 · Skills" — the only step kicker.
 *   - Serif h2 / sans body intro (`Pick what agents can do on …`).
 *   - `SkillsList` — receives the merged `SkillRecommendation[]` from
 *     `recommendSkillsForDetection(detected)` + `mergeLlmIntoBaseline`.
 *     The existing per-toggle `skills.saveForSite` persistence is
 *     unchanged.
 *   - Continue button: "Continue to install" (lime, sans).
 *
 * Detection is fired here once per active-site mount via the same
 * `detectPlatform` endpoint Step 3 uses; the call is server-side
 * idempotent. Failure / 503 / dormant collapses to a null detection,
 * which the helper maps to an empty recommendation list (no panel) —
 * the merchant continues with the seven defaults exactly as today.
 *
 * No Continue-to-skills-from-skills loop — only Continue-to-install.
 *
 * ADR-0226 — layout by site type (`classifyOnboardingSkillsLayout`):
 *   - store (Shopify / WooCommerce / cart platforms, or a connected
 *     store / eligible catalog): unchanged — the store skills grid.
 *   - non-store website (the default — includes detection in flight,
 *     the platform read in flight, detected non-store platforms, and
 *     the no-scan-yet in-dashboard Add site path): a "Business skills"
 *     group built from the business pages the scan found (detected URL
 *     prefilled, nothing on until the merchant clicks), then the store
 *     skills under a CLOSED "More skills" expander with nothing pre-
 *     selected. The expander stays closed on its own — a merchant on a
 *     non-store site should never see the ecommerce grid flashed open
 *     as the pre-detection fallback (founder 2026-10-21 on
 *     siteforbiz.com).
 * Both entry paths reach this step: in-account Add site, and the sill.so
 * hero → signup → auto-created site (ADR-0186 S1 resumes a
 * `pending_proof` site at Skills).
 */

import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown, ChevronRight, Info, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { ProfileExtractionResult, ReadinessPlatformKind } from '@sill/types';
import type { DetectPlatformResult } from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import { useSites } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { Card, NumLabel, Tooltip } from '../../components/atoms/index.js';
import { useProfileExtraction } from '../../hooks/useProfileExtraction.js';
import { useSiteScan } from '../../hooks/useSiteScan.js';
import { derivePlatformKind } from '../../lib/platform-kind.js';
import {
  classifyOnboardingSkillsLayout,
  mergeLlmIntoBaseline,
  mergeScanIntoBaseline,
  recommendSkillsForDetection,
  suggestBusinessSkillsFromScan,
} from '../../lib/skill-recommendations.js';
import type { SkillRecommendation } from '../../lib/skill-recommendations.js';
import { BusinessSkillSuggestions } from './BusinessSkillSuggestions.js';
import { DomainChip } from './DomainChip.js';
import { SkillsList } from './SkillsList.js';
import type { SkillsListHandle } from './SkillsList.js';

const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface SkillsStepProps {
  onNext: () => void;
}

export function SkillsStep({ onNext }: SkillsStepProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const { activeSite } = useSites();
  // The hook stays mounted: when ADR-0040's flag flips, its
  // `suggestedSkills` populates and feeds the `mergeLlmIntoBaseline`
  // seam below. Today (flag off in prod) the hook returns
  // `isAvailable: false`; the merge degrades to identity on the
  // detection-only baseline.
  const extraction = useProfileExtraction(activeSite?.site_id);
  // ADR-0186 S4 — the LIVE scan grounding. The scan is adopted at site
  // creation (S3), so a fresh onboarding usually has a completed row
  // waiting; if it is still running (the merchant clicked through
  // faster than the ~4s scan), the hook polls and the recommendations
  // upgrade in place when it lands. `isAvailable: false` (flag off) or
  // a failed scan leaves the detection baseline in charge — never empty.
  const siteScan = useSiteScan(activeSite?.site_id);
  // `undefined` = detection in flight; `null` = it failed / dormant.
  const [detected, setDetected] = useState<DetectPlatformResult | null | undefined>(undefined);
  // ADR-0226 — the same strict store/website helper Settings → Skills
  // uses. `null` while resolving; fail-soft to `website` like Settings.
  const [platformKind, setPlatformKind] = useState<ReadinessPlatformKind | null>(null);
  // "More skills" starts closed on a non-store site, and the UI never
  // auto-opens it — founder 2026-10-21: on siteforbiz.com (a non-
  // ecommerce site, no scan yet) the previous auto-open fallback made
  // the seven ecommerce skills visually first exactly where business
  // skills should have been. If there is nothing in the business block,
  // the merchant sees the quiet "Add business links in Settings" line
  // with the collapsed expander below; opening the ecommerce grid is
  // their explicit click, not ours.
  const [moreOpen, setMoreOpen] = useState(false);

  // Fire detection once per active-site mount. The endpoint is
  // server-side idempotent so a re-mount on theme toggle re-fires
  // without side effect. Detection is a HINT — on failure / 503 /
  // dormant we leave `detected` null and the suggestion panel hides.
  useEffect(() => {
    if (activeSite === null) return;
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const result = await api.sites.detectPlatform(activeSite.site_id);
        if (cancelled) return;
        setDetected(result);
      } catch {
        if (cancelled) return;
        setDetected(null);
      }
    })();
    void api.visibility.getReport(activeSite.site_id).then(
      (r) => {
        if (!cancelled) setPlatformKind(derivePlatformKind({ install: null, visibility: r }));
      },
      () => {
        if (!cancelled) setPlatformKind('website');
      },
    );
    return (): void => {
      cancelled = true;
    };
  }, [activeSite, api]);

  // ADR-0226 — business pages the scan found, and the layout they drive.
  const businessSuggestions = useMemo(
    () => suggestBusinessSkillsFromScan(siteScan.scan),
    [siteScan.scan],
  );
  const layout = classifyOnboardingSkillsLayout({
    detected,
    platform_kind: platformKind,
    business_suggestion_count: businessSuggestions.length,
    scan_platform: siteScan.scan?.detected_platform ?? null,
  });
  const scanning = siteScan.isAvailable && siteScan.status === 'running';

  // ADR-0050 §2.3 — compose the LLM enrichment into the detection
  // baseline. When `extraction.suggestedSkills` is undefined (the
  // dormant / flag-off / pre-run state, which is ALL of prod today)
  // the merge is the identity on the detection-only baseline.
  const baseline = useMemo(
    () => recommendSkillsForDetection(detected ?? null),
    [detected],
  );
  const llmResult: ProfileExtractionResult | null = useMemo(() => {
    if (extraction.status !== 'complete') return null;
    if (extraction.suggestedSkills === undefined) return null;
    return {
      profile_extraction_id: '',
      status: 'complete',
      suggested_skills: extraction.suggestedSkills,
    };
  }, [extraction.status, extraction.suggestedSkills]);
  // Layer order (weakest → strongest): detection baseline → LIVE scan
  // grounding (ADR-0186 S4) → dormant ADR-0040 LLM (identity in prod).
  const scanGrounded = useMemo(
    () => mergeScanIntoBaseline(baseline, siteScan.scan),
    [baseline, siteScan.scan],
  );
  const recommendations = useMemo(
    () => mergeLlmIntoBaseline(scanGrounded, llmResult),
    [scanGrounded, llmResult],
  );
  // ADR-0226 — on a non-store site the store skills sit behind a closed
  // expander, so they are NOT pre-selected: committing a pre-toggled set
  // the merchant never saw on Continue would be an auto-apply in all but
  // name (ADR-0040). They start off; the merchant opens "More skills" and
  // turns on what they want.
  const NO_RECOMMENDATIONS: readonly SkillRecommendation[] = useMemo(() => [], []);
  const gridRecommendations = layout === 'website' ? NO_RECOMMENDATIONS : recommendations;

  const domain = activeSite?.domain ?? 'your site';
  const skillsListRef = useRef<SkillsListHandle>(null);

  // Commit a detection-narrowed display the merchant never manually
  // touched — an explicit click (Continue), not an auto-apply. See
  // `SkillsList`'s "Two accuracy fixes" docblock.
  const handleContinue = (): void => {
    // Never commit a grid the merchant could not see (ADR-0226): on a
    // non-store site the store skills are behind the expander.
    if (layout !== 'website') skillsListRef.current?.commitPendingSkills();
    onNext();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <NumLabel n={2}>Step 2 · Skills</NumLabel>
      {/* Condensed header (founder 2026-08-04): the step headings were
          oversized next to their kickers — moderate serif, tighter gap. */}
      <h2
        style={{
          fontFamily: serifFont,
          fontSize: 'clamp(26px, 4vw, 34px)',
          lineHeight: 1.05,
          color: C.text,
          letterSpacing: -1,
          marginBottom: 12,
        }}
      >
        Pick what agents can do on <em>{domain}</em>.
      </h2>

      {/* ADR-0186 S5 — the one domain-edit surface: a change link while
          pending_proof; editing re-grounds the scan server-side. */}
      <DomainChip />

      {/* ADR-0186 S4 — staged-reveal scan strip. The scan started at
          email capture (sill.so hero), so this is continuity, not a new
          wait: while it finishes, the detection defaults already render
          below; when it lands the suggestions upgrade in place. Hidden
          entirely when the scan is unavailable or already surfaced its
          result silently. */}
      {siteScan.isAvailable && siteScan.status === 'running' ? (
        <div
          className="flex items-center gap-2 mb-4"
          style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}
          data-testid="scan-running-strip"
        >
          <RefreshCw size={12} style={{ animation: 'spin 1.4s linear infinite' }} />
          Reading <span style={{ color: C.text }}>{domain}</span>…
        </div>
      ) : null}

      {/* One intro line (founder 2026-07-14: the scan-complete strip +
          two stacked paragraphs were redundant). The "what turning a
          skill on actually does" detail moved into the (i) tooltip. */}
      {/* Founder 2026-10-11: on a website with nothing found, the intro
          only repeated the heading ("Choose what agents can do."). */}
      {layout === 'website' && businessSuggestions.length === 0 && scanning ? null : (
      <div
        className="flex items-start gap-2"
        style={{ maxWidth: 640, marginBottom: 24 }}
      >
        <p
          style={{
            fontFamily: sansFont,
            fontSize: 'clamp(14px, 1.6vw, 16px)',
            color: C.muted,
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          {layout === 'website' ? (
            businessSuggestions.length > 0 ? (
              <>
                Found on <span style={{ color: C.text }}>{domain}</span>.
              </>
            ) : (
              <>Add the pages agents should send people to.</>
            )
          ) : recommendations.length > 0 ? (
            <>
              We read <span style={{ color: C.text }}>{domain}</span> and
              turned on the skills that fit. Checkout skills stay off until
              you connect a payment path — adjust anything below.
            </>
          ) : (
            // No-detection case: just the instruction — leading with "we
            // didn't detect a store, nothing's pre-selected" read as an
            // apology, not guidance (founder 2026-08-04).
            <>Turn on the skills you want to expose to agents.</>
          )}
        </p>
        <Tooltip content="Turning a skill on here makes it available to configure — some also need a catalog, a fulfillment connector, or a way to accept payment before agents can actually call them. You'll see exactly what's live and what's still needed after install.">
          <Info
            size={15}
            style={{ color: C.muted, cursor: 'help', flexShrink: 0, marginTop: 3 }}
          />
        </Tooltip>
      </div>
      )}

      {/* Skills list — defaults + detection-driven suggestion panel.
          The merged `recommendations` flow in as a prop; SkillsList
          renders the suggestion panel when the list is non-empty
          and keeps its existing per-toggle save semantics. The
          classifier defaults to `'website'` while detection + the
          platform read are still in flight, so there is no pre-
          detection placeholder: a non-store site never sees the
          ecommerce grid flashed as a fallback (founder 2026-10-21). */}
      {activeSite !== null && layout === 'store' ? (
        <Card className="p-5 mb-6">
          <SkillsList
            ref={skillsListRef}
            extraction={extraction}
            recommendations={gridRecommendations}
          />
        </Card>
      ) : null}

      {/* ADR-0226 — non-store site: business-skill suggestions first,
          the store skills tucked under a closed "More skills" expander.
          The grid stays MOUNTED while closed (hidden) so a toggle the
          merchant made survives closing it. */}
      {activeSite !== null && layout === 'website' ? (
        <Card className="p-5 mb-6">
          <BusinessSkillSuggestions
            site_id={activeSite.site_id}
            domain={domain}
            suggestions={businessSuggestions}
            scanning={scanning}
          />
          <div
            className="mt-5 pt-4"
            style={{ borderTop: `1px solid ${C.border}` }}
          >
            <button
              type="button"
              aria-expanded={moreOpen}
              aria-controls="onboarding-more-skills"
              onClick={() => setMoreOpen((v) => !v)}
              className="flex items-center justify-between w-full"
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textAlign: 'left',
              }}
              data-testid="more-skills-toggle"
            >
              <span>
                <span
                  style={{
                    display: 'block',
                    fontFamily: sansFont,
                    fontSize: 14,
                    fontWeight: 500,
                    color: C.text,
                  }}
                >
                  More skills
                </span>
                <span
                  style={{
                    display: 'block',
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.muted,
                    lineHeight: 1.5,
                    marginTop: 2,
                  }}
                >
                  Catalog, availability, orders.
                </span>
              </span>
              {moreOpen ? (
                <ChevronDown size={14} color={C.muted} />
              ) : (
                <ChevronRight size={14} color={C.muted} />
              )}
            </button>
            <div
              id="onboarding-more-skills"
              hidden={!moreOpen}
              style={{ marginTop: 16 }}
              data-testid="more-skills-panel"
            >
              <SkillsList
                ref={skillsListRef}
                extraction={extraction}
                recommendations={gridRecommendations}
              />
            </div>
          </div>
        </Card>
      ) : null}

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <button
          type="button"
          onClick={handleContinue}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5"
          style={{
            background: C.lime,
            color: C.ink,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 14,
            borderRadius: 2,
          }}
        >
          Continue to install <ArrowRight size={15} />
        </button>
      </div>
    </motion.div>
  );
}
